
import React, { forwardRef, useEffect } from 'react';
import { FieldWrapper } from '@progress/kendo-react-form';
import { Label, Hint } from '@progress/kendo-react-labels';
import { Input, NumericTextBox, Checkbox, TextArea, ColorPicker, RadioButton } from "@progress/kendo-react-inputs";
import { DatePicker, TimePicker, DateTimePicker } from "@progress/kendo-react-dateinputs";
import { ComboBox } from "@progress/kendo-react-dropdowns";
import { Button } from "@progress/kendo-react-buttons";
import { LocalizationProvider, loadMessages } from '@progress/kendo-react-intl';
import HelpPopup from 'views/widgets/HelpPopup.jsx';

const InputView = forwardRef((props, ref) => {

	const { field, type, label, hideLabel, value, customInput,
		valid, disabled, readOnly, required, hidden,
		maxlength, min, max, step = 1, views = ['gradient'],
		data, dataItemKey, defaultValue, textField,
		hint, placeholder, loading,
		onChange, onKeyPress, onBlur, format, style,
		labelWidth, singleLineWrapper, textAreaLineWrapper, textAreaHeight,
		configureButtonClick, configureButtonClass, configureButtonTitle,
		autoFocusOnLoad, autoSelectOnFocus = true, taRows = 1, className,
		fieldHelp, fieldHelpClass,
		resetColorButton, onColorReset } = props;
	const val = (value == null ? undefined : value);

	const setFocus = () => {
		if (ref && ref.current && !readOnly && !disabled) ref.current.focus();
	};

	const onFocus = (e) => {
		if (autoSelectOnFocus) {
			// kick off selection in another thread to deal with asynchronouse issues
			const target = e.currentTarget;
			if (target)
				setTimeout(() => {
					target.select();
				}, 0);
		}
	};

	const messageObj = {
		'dropdowns.nodata': 'No records available',
	}
	loadMessages(messageObj, 'en');

	const appliedLabelWidth = labelWidth - (fieldHelp ? 17 : 0);
	const labelElem = (
		<>
			<Label
				className={type === "checkbox" ? "" : "constituo-label"}
				editorId={field}
				editorDisabled={disabled}
				style={labelWidth ? { width: appliedLabelWidth + "px", float: "left" } : {}}
			>
				{label}
			</Label>
			{fieldHelp &&
				<HelpPopup text={fieldHelp} cssClass={fieldHelpClass} />
			}
		</>
	);

	useEffect(() => {
		autoFocusOnLoad && setFocus();
	}, []);

	let inputElem;
	if (type === 'text' || type === 'password')
		inputElem =
			<Input
				style={singleLineWrapper ? { width: "calc(100% - " + labelWidth + "px)", float: "left", height: "34px" } : { height: "34px" }}
				id={field}
				ref={ref}
				type={type}
				disabled={disabled}
				readOnly={readOnly}
				maxLength={maxlength}
				required={required}
				aria-required={required ? "true" : undefined}
				validityStyles={true}
				value={val}
				valid={valid}
				onChange={onChange}
				onKeyPress={onKeyPress}
				onFocus={onFocus}
				onBlur={onBlur}
				onContextMenu={() => { }}
				className={className}
				autoComplete={type === "password" ? "new-password" : undefined} />
				
	else if (type === 'file')
		inputElem = 
			<div className="constituo-file-field">
				{/* Hidden File Input */}
      			<input 
        			type="file" 
        			ref={ref} 
        			style={{ display: "none" }} 
        			onChange={onChange} 
      			/>				
      			{/* Custom Button to Open File Dialog */}
      			<Button
      				onClick={() => ref.current.click()}>
        			{value ? "Change File" : "Choose File"}
      			</Button>
      			{/* Display Selected File Name */}
      			{value && <span style={{paddingLeft: "10px"}}>{value.name}</span>}
      		</div>

	else if (type === 'configurable')
		inputElem =
			<span className="constituo-configurable-field">
				<Input
					style={singleLineWrapper ? { width: "calc(100% - " + labelWidth + "px)", float: "left" } : {}}
					id={field}
					ref={ref}
					type={"text"}
					disabled={disabled}
					readOnly={true}
					maxLength={maxlength}
					required={required}
					aria-required={required ? "true" : undefined}
					validityStyles={true}
					value={val}
					valid={valid} />
				<Button
					onClick={configureButtonClick}
					iconClass={configureButtonClass}
					title={configureButtonTitle} />
			</span>
			
	else if (type === 'custom')
		inputElem = customInput;

	else if (type === 'number')
		inputElem =
			<NumericTextBox
				style={singleLineWrapper ? { width: "calc(100% - " + labelWidth + "px)", float: "left" } : {}}
				id={field}
				ref={ref}
				disabled={disabled}
				readOnly={readOnly}
				required={required}
				aria-required={required ? "true" : undefined}
				validityStyles={true}
				value={val}
				valid={valid}
				min={min}
				max={max}
				step={step}
				defaultValue={defaultValue}
				format={format}
				onChange={onChange}
				onKeyPress={onKeyPress}
				onFocus={onFocus} />

	else if (type === 'date')
		inputElem =
			<DatePicker
				style={singleLineWrapper ? { width: "calc(100% - " + labelWidth + "px)", float: "left" } : {}}
				id={field}
				ref={ref}
				disabled={disabled}
				readOnly={readOnly}
				required={required}
				aria-required={required ? "true" : undefined}
				valid={valid}
				validityStyles={true}
				value={val}
				format={format}
				onChange={onChange} />

	else if (type === 'time')
		inputElem =
			<TimePicker
				style={singleLineWrapper ? { width: "calc(100% - " + labelWidth + "px)", float: "left" } : {}}
				id={field}
				ref={ref}
				disabled={disabled}
				readOnly={readOnly}
				required={required}
				aria-required={required ? "true" : undefined}
				valid={valid}
				validityStyles={true}
				value={val}
				format={format}
				onChange={onChange} />
	
	else if (type === 'timestamp')
		inputElem = 
			<DateTimePicker
				style={singleLineWrapper ? { width: "calc(100% - " + labelWidth + "px)", float: "left" } : {}}
				id={field}
				ref={ref}
				disabled={disabled}
				readOnly={readOnly}
				required={required}
				aria-required={required ? "true" : undefined}
				valid={valid}
				validityStyles={true}
				value={val}
				format={format}
				onChange={onChange} />			

	else if (type === 'textarea'){
		var wrapperStyle;
		if(singleLineWrapper){
			wrapperStyle =
			{
	        	width: `calc(100% - ${labelWidth}px)`,
	        	float: "left",            // ✅ sit on the same line as the label
      		}
		} else if(textAreaLineWrapper){
			wrapperStyle =
			{
	        	width: `calc(100% - ${labelWidth}px)`,
	        	float: "left",            // ✅ sit on the same line as the label
	        	height: `${textAreaHeight}px`
      		}
		} else{
	    	wrapperStyle = {
	        	width: "100%", 
	        	height: "100%"
	        }
      	};
      	
      	inputElem =
			<TextArea
				style={wrapperStyle}
				id={field}
				ref={ref}
				disabled={disabled}
				readOnly={readOnly}
				maxLength={maxlength}
				required={required}
				aria-required={required ? "true" : undefined}
				valid={valid}
				validityStyles={true}
				value={val}
				aria-label={label}
				rows={taRows}
				placeholder={placeholder}
				onChange={onChange}
				className={className}
			/>
	}
	else if (type === 'combobox')
		inputElem =
			<LocalizationProvider language='en'>
				<ComboBox
					style={singleLineWrapper ? { width: "calc(100% - " + labelWidth + "px)", float: "left" } : {}}
					id={field}
					ref={ref}
					disabled={disabled}
					readOnly={readOnly}
					required={required}
					aria-required={required ? "true" : undefined}
					valid={valid}
					validityStyles={true}
					value={val}
					placeholder={placeholder}
					textField={textField}
					data={data}
					dataItemKey={dataItemKey}
					defaultValue={defaultValue}
					clearButton={false}
					onChange={onChange}
					popupSettings={{animate: false}}
					skipDisabledItems={true} />
			</LocalizationProvider>

	else if (type === 'radio') {
		let radioButtons = data.map((item) =>
			<span key={item[dataItemKey]}>
				<RadioButton
					style={{ marginRight: "3px" }}
					key={item[dataItemKey] + "_radio"}
					name={field}
					disabled={disabled}
					required={required}
					aria-required={required ? "true" : undefined}
					aria-label={label + " " + item[textField]}
					value={item[dataItemKey]}
					checked={item[dataItemKey] === val[dataItemKey]}
					label={item[textField]}
					onChange={(e) => onChange({ ...e, value: item })}
				/>
				<br />
			</span>
		);
		inputElem =
			<div className="constituo-radio-field">
				{radioButtons}
			</div>

	} else if (type === 'label') {
		inputElem =
			<span className="constituo-label-field">{val}</span>

	} else if (type === 'checkbox')
		inputElem =
			<Checkbox
				style={singleLineWrapper ? { float: "left", lineHeight: "36px", marginRight: "10px" } : {}}
				className="constituo-checkbox-field"
				id={field}
				ref={ref}
				disabled={disabled}
				required={required}
				aria-required={required ? "true" : undefined}
				valid={valid}
				validityStyles={true}
				value={val}
				onChange={onChange} />

	else if (type === 'checkmark')
		inputElem =
			<span
				style={singleLineWrapper ? { float: "left" } : {}}
				className="constituo-checkmark-field">
				{val ? <span className="fa fa-check" /> : <span className="fa fa-check-empty" /> }
			</span>

	else if (type === 'color') { 		
  		const wrapperStyle = singleLineWrapper
    	? {
        	width: `calc(100% - ${labelWidth}px)`,
        	float: "left",            // ✅ sit on the same line as the label
        	display: "flex",
        	alignItems: "center",
        	height: "34px",
      	} : {
        	display: "flex",
        	alignItems: "center",
        	height: "34px",
      	};

  		inputElem = (
    		<div className="constituo-color-picker-field" style={wrapperStyle}>
      			<ColorPicker
					ariaLabel={label}
        			style={{ width: "100%", height: "34px", zIndex: 5000 }}
        			size="small"
        			fillMode="solid"
        			id={field}
        			disabled={disabled}
        			valid={valid}
        			validityStyles={true}
        			 views={views}
        			defaultValue={defaultValue}
        			value={val}
        			onChange={onChange}
      			/>
				{resetColorButton && (
					<Button
						onClick={onColorReset}
						iconClass={"fa fa-fw fa-small fa-ccw fa-rotate-270"}
						title={"Reset Color"} 
						style={{
							marginLeft: "2px",
							fontSize: "14pt"
						}}
					/>
				)}
    		</div>
  		);
	
	} else
		inputElem = null;

	if (loading)
		inputElem = (
			<span
				style={{ width: "100px", justifyContent: "center", overflow: hidden }}
				className="fa fa-lg fa-fw fa-spin fa-cs-logo constituo-mask"
			/>
		);


	if (hidden)
		return null;
	else
		return (
			<FieldWrapper className={"constituo-field-wrapper" + (singleLineWrapper ? " constituo-single-line-wrapper" : "")} style={{ ...style }}>
				{!hideLabel && labelElem}
				{inputElem}
				{hint ?
					<Hint
						direction={"end"}
					>
						{hint}
					</Hint>
					: null}
			</FieldWrapper>
		);
});

export default InputView;
